import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Video, FlaskConical, Wrench, BookOpen, Book, Clock, ArrowRight, Sparkles, BookMarked, Newspaper } from 'lucide-react';
import ContentCard from '../components/ContentCard';
import NewsCarousel from '../components/NewsCarousel';
import { curiosityAPI } from '../services/curiosityAPI';
import { trackContentEvent } from '@/analytics/analytics';

/**
 * Chapter Curiosity View
 * Path: /curiosity/chapter/{chapterId}
 * 
 * Layout Sections:
 * A. Core Content Section - Grid of content cards
 * B. Deep Content Section - Grid of content cards
 * C. Explore More Topics Section - Grid of topic cards
 * D. Related News Section - Horizontal scrollable carousel
 */
export default function ChapterCuriosity() {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chapterData, setChapterData] = useState(null);
  const [coreContent, setCoreContent] = useState({
    videos: [],
    simulations: [],
    tools: [],
    courses: [],
    books: [],
  });
  const [deepContent, setDeepContent] = useState({
    videos: [],
    simulations: [],
    tools: [],
    courses: [],
    books: [],
  });
  const [exploreTopics, setExploreTopics] = useState([]);
  const [relatedNews, setRelatedNews] = useState([]);
  const timeSpentIntervalRef = useRef(null);

  useEffect(() => {
    if (chapterId) {
      loadChapterContent();
    }
    // Start periodic page time_spent heartbeat
    try {
      if (timeSpentIntervalRef.current) clearInterval(timeSpentIntervalRef.current);
      timeSpentIntervalRef.current = setInterval(() => {
        try {
          trackContentEvent('page_time_spent', {
            page: 'chapter_curiosity',
            chapter_id: chapterId ? parseInt(chapterId) : null,
            subject_id: chapterId ? parseInt(chapterId) : null,
            time_spent_seconds: 15,
          });
        } catch (e) {}
      }, 15000);
    } catch (e) {}

    return () => {
      try { if (timeSpentIntervalRef.current) clearInterval(timeSpentIntervalRef.current); } catch (e) {}
    };
  }, [chapterId]);

  const loadChapterContent = async () => {
    try {
      setLoading(true);
      
      const response = await curiosityAPI.getChapterContent(chapterId);
      
      setChapterData(response.chapter || null);
      setCoreContent(response.core_content || {
        videos: [],
        simulations: [],
        tools: [],
        courses: [],
        books: [],
      });
      setDeepContent(response.deep_content || {
        videos: [],
        simulations: [],
        tools: [],
        courses: [],
        books: [],
      });
      setExploreTopics(response.explore_topics || []);
      setRelatedNews(response.related_news || []);
    } catch (error) {
      console.error('Failed to load chapter content:', error);
      setError(error.message || 'Failed to load chapter content. Please try again.');
      // Set empty state on error
      setChapterData(null);
      setCoreContent({});
      setDeepContent({});
      setExploreTopics([]);
      setRelatedNews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleContentClick = (content) => {
    try {
      trackContentEvent('content_play', {
        content_id: content?.id ?? null,
        content_type: content?.content_type ?? null,
        content_title: content?.title ?? content?.file_name ?? null,
        subject_id: chapterId || null,
        chapter_id: chapterId || null,
      });
    } catch (e) {}
    if (content.content_type === 'video') {
      navigate(`/student/curiosity/video/${content.id}`);
    } else {
      navigate(`/student/curiosity/content/${content.id}`);
    }
  };

  const handleTopicClick = (topicId) => {
    navigate(`/student/curiosity/topic/${topicId}`);
  };

  const renderContentGrid = (contentList, title, icon) => {
    if (!contentList || contentList.length === 0) return null;

    const Icon = icon;
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          {Icon && <Icon className="h-5 w-5 text-[hsl(var(--green-bright))]" />}
          <h3 className="text-xl font-semibold text-foreground">{title}</h3>
          <Badge variant="outline" className="ml-2">
            {contentList.length}
          </Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {contentList.map((content) => (
            <ContentCard
              key={content.id}
              content={content}
              onClick={() => handleContentClick(content)}
            />
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-y-auto">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--green-bright))]"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-y-auto">
        <div className="max-w-7xl mx-auto w-full px-6 py-12">
          <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
            <CardContent className="p-6 text-center space-y-4">
              <p className="text-muted-foreground text-lg">{error}</p>
              <Button 
                onClick={() => {
                  setError(null);
                  loadChapterContent();
                }}
                className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))]"
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const hasCoreContent = Object.values(coreContent).some(arr => arr && arr.length > 0);
  const hasDeepContent = Object.values(deepContent).some(arr => arr && arr.length > 0);

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-y-auto">
      <div className="max-w-7xl mx-auto w-full px-6 py-6 space-y-8">
        {/* Chapter Header */}
        {chapterData && (
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="h-8 w-8 text-[hsl(var(--green-bright))]" />
              <h1 className="text-3xl font-bold text-foreground">{chapterData.name}</h1>
            </div>
            {chapterData.description && (
              <p className="text-muted-foreground text-lg max-w-3xl">{chapterData.description}</p>
            )}
          </div>
        )}

        {/* Core Resources Section */}
        {hasCoreContent && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-1 w-12 bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] rounded-full"></div>
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-[hsl(var(--green-bright))]" />
                Core Resources
              </h2>
            </div>
            <div className="space-y-6">
              {renderContentGrid(coreContent.videos, 'Videos', Video)}
              {renderContentGrid(coreContent.simulations, 'Simulations', FlaskConical)}
              {renderContentGrid(coreContent.tools, 'Tools', Wrench)}
              {renderContentGrid(coreContent.courses, 'Courses', BookOpen)}
              {renderContentGrid(coreContent.books, 'Books', Book)}
            </div>
          </div>
        )}

        {/* Deep Dive Section */}
        {hasDeepContent && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-1 w-12 bg-gradient-to-r from-[hsl(var(--purple-accent))] to-[hsl(var(--teal-vivid))] rounded-full"></div>
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-[hsl(var(--purple-accent))]" />
                Deep Dive
              </h2>
            </div>
            <div className="space-y-6">
              {renderContentGrid(deepContent.videos, 'Videos', Video)}
              {renderContentGrid(deepContent.simulations, 'Simulations', FlaskConical)}
              {renderContentGrid(deepContent.tools, 'Tools', Wrench)}
              {renderContentGrid(deepContent.courses, 'Courses', BookOpen)}
              {renderContentGrid(deepContent.books, 'Books', Book)}
            </div>
          </div>
        )}

        {/* Explore More Topics Section */}
        {exploreTopics.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-1 w-12 bg-gradient-to-r from-[hsl(var(--orange-warm))] to-[hsl(var(--green-bright))] rounded-full"></div>
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <BookMarked className="h-6 w-6 text-[hsl(var(--orange-warm))]" />
                Explore More Topics
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {exploreTopics.map((topic) => (
                <Card
                  key={topic.id}
                  className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] cursor-pointer hover:border-[hsl(var(--primary))] hover:shadow-lg transition-all"
                  onClick={() => handleTopicClick(topic.id)}
                >
                  <CardContent className="p-0">
                    {topic.thumbnail_url && (
                      <div className="w-full h-40 overflow-hidden rounded-t-lg">
                        <img
                          src={topic.thumbnail_url}
                          alt={topic.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            (e.target).style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-lg mb-2 text-foreground">{topic.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {topic.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-xs">
                          {topic.resources?.length || 0} Resources
                        </Badge>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Related News Section */}
        {relatedNews.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-1 w-12 bg-gradient-to-r from-[hsl(var(--teal-vivid))] to-[hsl(var(--green-bright))] rounded-full"></div>
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Newspaper className="h-6 w-6 text-[hsl(var(--teal-vivid))]" />
                Related News
              </h2>
            </div>
            <NewsCarousel news={relatedNews} />
          </div>
        )}

        {/* Empty State */}
        {!hasCoreContent && !hasDeepContent && exploreTopics.length === 0 && relatedNews.length === 0 && (
          <div className="text-center py-12">
            <Sparkles className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Content Available</h3>
            <p className="text-muted-foreground">
              Content for this chapter is being prepared. Check back soon!
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

